// Funções para gerenciamento de autenticação

import { devLog } from '../utils/devLog';

// Chaves para armazenamento no localStorage (separadas por contexto)
const PARISH_TOKEN_KEY = 'eventos_catolicos_parish_token';
const PARISH_USER_KEY = 'eventos_catolicos_parish_user';
const ADMIN_TOKEN_KEY = 'eventos_catolicos_admin_token';
const ADMIN_USER_KEY = 'eventos_catolicos_admin_user';

// Contexto atual de autenticação
export type AuthContext = 'parish' | 'admin';

// Função para detectar o contexto atual baseado na URL
export const getCurrentAuthContext = (): AuthContext => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  devLog('🔍 [getCurrentAuthContext] Hostname:', hostname);
  devLog('🔍 [getCurrentAuthContext] Pathname:', pathname);

  // Se estiver no subdomínio admin
  if (hostname.includes('admin') || hostname.startsWith('admin.')) {
    devLog('🔍 [getCurrentAuthContext] Contexto detectado: admin (subdomínio)');
    return 'admin';
  }

  // Se estiver no subdomínio parish
  if (hostname.includes('parish') || hostname.startsWith('parish.')) {
    devLog('🔍 [getCurrentAuthContext] Contexto detectado: parish (subdomínio)');
    return 'parish';
  }

  // Se estiver na rota admin (sem subdomínio)
  if (pathname.includes('/admin') || pathname.startsWith('/admin')) {
    devLog('🔍 [getCurrentAuthContext] Contexto detectado: admin (rota)');
    return 'admin';
  }

  // Verificar se há token admin salvo (fallback)
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  const parishToken = localStorage.getItem(PARISH_TOKEN_KEY);

  if (adminToken && !parishToken) {
    devLog('🔍 [getCurrentAuthContext] Contexto detectado: admin (token encontrado)');
    return 'admin';
  }

  devLog('🔍 [getCurrentAuthContext] Contexto detectado: parish (padrão)');
  return 'parish';
};

// Interface para o usuário
export interface User {
  id: string;
  name: string;
  email: string;
  userType: 'user' | 'parish_admin' | 'admin';
  parishId?: string;
}

// Função para obter as chaves baseadas no contexto
const getKeys = (context: AuthContext = getCurrentAuthContext()) => {
  if (context === 'admin') {
    return {
      tokenKey: ADMIN_TOKEN_KEY,
      userKey: ADMIN_USER_KEY
    };
  }
  return {
    tokenKey: PARISH_TOKEN_KEY,
    userKey: PARISH_USER_KEY
  };
};

// Função para salvar o token no localStorage
export const setToken = (token: string, context?: AuthContext): void => {
  const { tokenKey } = getKeys(context);
  localStorage.setItem(tokenKey, token);
};

// Função para obter o token do localStorage
export const getToken = (context?: AuthContext): string | null => {
  const { tokenKey } = getKeys(context);
  return localStorage.getItem(tokenKey);
};

// Função para remover o token do localStorage (logout)
export const removeToken = (context?: AuthContext): void => {
  const { tokenKey } = getKeys(context);
  localStorage.removeItem(tokenKey);
};

// Função para salvar os dados do usuário no localStorage
export const setUser = (user: User, context?: AuthContext): void => {
  const { userKey } = getKeys(context);
  localStorage.setItem(userKey, JSON.stringify(user));
};

// Função para obter os dados do usuário do localStorage
export const getUser = (context?: AuthContext): User | null => {
  const { userKey } = getKeys(context);
  const user = localStorage.getItem(userKey);
  return user ? JSON.parse(user) : null;
};

// Função para remover os dados do usuário do localStorage (logout)
export const removeUser = (context?: AuthContext): void => {
  const { userKey } = getKeys(context);
  localStorage.removeItem(userKey);
};

// Função para verificar se o usuário está autenticado
export const isAuthenticated = (context?: AuthContext): boolean => {
  return !!getToken(context);
};

// Função para fazer logout (remover token e dados do usuário)
export const logout = (context?: AuthContext): void => {
  removeToken(context);
  removeUser(context);
};

// Função para fazer logout de todos os contextos
export const logoutAll = (): void => {
  removeToken('parish');
  removeUser('parish');
  removeToken('admin');
  removeUser('admin');
};

// Função para limpar tokens expirados de outros contextos
export const clearOtherContextTokens = (currentContext: AuthContext): void => {
  const otherContext = currentContext === 'admin' ? 'parish' : 'admin';

  devLog(`🧹 [clearOtherContextTokens] Contexto atual: ${currentContext}, limpando: ${otherContext}`);

  removeToken(otherContext);
  removeUser(otherContext);
};



const authService = {
  setToken,
  getToken,
  removeToken,
  setUser,
  getUser,
  removeUser,
  isAuthenticated,
  logout,
  logoutAll,
  clearOtherContextTokens,
  getCurrentAuthContext
};

export default authService;
